import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

// --- Fleet Overview types ---

export interface FleetStats {
  total_repos: number;
  archived_repos: number;
  forked_repos: number;
  ci_distribution: Record<string, number>;
  activity_distribution: Record<string, number>;
  vulnerabilities: {
    total_critical: number;
    total_high: number;
    total_medium: number;
    total_low: number;
  };
}

export interface FleetRepoRow {
  id: number;
  full_name: string;
  org_name: string;
  ci_status: string | null;
  activity_class: string | null;
  dependabot_critical: number | null;
  dependabot_high: number | null;
  structure_score: number | null;
  deploy_target: string | null;
  branch_model: string | null;
  backstage_owner: string | null;
  runtime_versions: Record<string, string> | null;
  databases: Array<{ name: string }> | null;
  container_images: Array<{ image: string }> | null;
}

// --- Tech Summary types ---

export interface LangEntry {
  language: string;
  repo_count: number;
}

export interface RuntimeEntry {
  runtime: string;
  version: string;
  repo_count: number;
}

export interface DbEntry {
  name: string;
  repo_count: number;
}

export interface DeployEntry {
  deploy_target: string;
  repo_count: number;
}

export interface VersionEntry {
  version: string;
  repo_count: number;
  runtime?: string;
}

export interface TechSummary {
  total_repos: number;
  total_repos_all: number;
  languages: LangEntry[];
  runtimes: RuntimeEntry[];
  databases: DbEntry[];
  deploy_targets?: DeployEntry[];
}

export interface TargetStack {
  java: string;
  node: string;
  python: string;
  dotnet: string;
  [k: string]: string;
}

export interface TechRepoRow {
  id: number;
  full_name: string;
  is_archived: boolean;
  activity_class: string | null;
  commits_90d: number | null;
  ci_status: string | null;
  dependabot_critical: number | null;
  dependabot_high: number | null;
  version?: string | null;
  runtime_versions?: Record<string, string> | null;
}

// --- Entity detail types ---

export interface CensusEntityDetail {
  full_name: string;
  org_name: string;
  is_archived: boolean;
  pushed_at: string | null;
  last_scanned_at: string | null;
  languages: Record<string, number> | null;
  frameworks: Array<{ name: string; version: string | null }> | null;
  build_system: string | null;
  dependabot_critical: number | null;
  dependabot_high: number | null;
  dependabot_medium: number | null;
  dependabot_low: number | null;
  code_scanning_errors: number | null;
  code_scanning_warnings: number | null;
  top_cves: Array<{ cve: string; severity: string; package: string }> | null;
  runtime_versions: Record<string, string> | null;
  databases: Array<{ name: string; version: string | null; source: string }> | null;
  container_images: Array<{
    image: string;
    base: string;
    tag: string;
    stage: string | null;
  }> | null;
  ci_system: string | null;
  ci_status: string | null;
  structure_score: number | null;
  test_coverage_class: string | null;
  deploy_target: string | null;
  activity_class: string | null;
  commits_90d: number | null;
  prs_merged_90d: number | null;
  last_commit_at: string | null;
  total_branches: number | null;
  stale_branches: number | null;
  branch_model: string | null;
  github_teams: string[] | null;
  backstage_owner: string | null;
  inferred_owner: string | null;
  confidence: string | null;
}

// --- Snapshot history types ---

export interface UiRepoSnapshot {
  id: number;
  taken_at: string;   // ISO-8601
  total_repos: number;
  ui_repos: number;
  rally_web_count: number;
}

// --- API interface ---

export interface CensusApi {
  getStats(params: { includeArchived: boolean }): Promise<FleetStats>;
  getRepos(params: {
    limit: number;
    offset: number;
    includeArchived: boolean;
  }): Promise<FleetRepoRow[]>;
  getTechnology(params: { includeArchived: boolean }): Promise<TechSummary>;
  getTargetStack(): Promise<TargetStack>;
  getVersionBreakdown(params: {
    lang: string;
    includeArchived: boolean;
  }): Promise<VersionEntry[]>;
  getDatabaseVersionBreakdown(params: {
    db: string;
    includeArchived: boolean;
  }): Promise<VersionEntry[]>;
  /** Generic filter for /api/v1/summary/technology/repos — pass URLSearchParams as a string. */
  getReposByFilter(
    queryParams: string,
    includeArchived: boolean,
  ): Promise<TechRepoRow[]>;
  getEntityDetail(repoId: string): Promise<CensusEntityDetail>;
  getUiRepoSnapshots(): Promise<UiRepoSnapshot[]>;
}

export const censusApiRef = createApiRef<CensusApi>({
  id: 'plugin.census.service',
});

export class CensusApiClient implements CensusApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxyUrl}/census`;
  }

  async getStats({
    includeArchived,
  }: {
    includeArchived: boolean;
  }): Promise<FleetStats> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/stats?include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(`Stats: HTTP ${r.status}`);
    return r.json();
  }

  async getRepos({
    limit,
    offset,
    includeArchived,
  }: {
    limit: number;
    offset: number;
    includeArchived: boolean;
  }): Promise<FleetRepoRow[]> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/repos?limit=${limit}&offset=${offset}&include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(`Repos: HTTP ${r.status}`);
    return r.json();
  }

  async getTechnology({
    includeArchived,
  }: {
    includeArchived: boolean;
  }): Promise<TechSummary> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/summary/technology?include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(`Technology summary: ${r.statusText}`);
    return r.json();
  }

  async getTargetStack(): Promise<TargetStack> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(`${base}/api/v1/config/target-stack`);
    if (!r.ok) throw new Error(`Target stack: ${r.statusText}`);
    return r.json();
  }

  async getVersionBreakdown({
    lang,
    includeArchived,
  }: {
    lang: string;
    includeArchived: boolean;
  }): Promise<VersionEntry[]> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/summary/technology/language/${encodeURIComponent(lang)}/versions?include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(r.statusText);
    return r.json();
  }

  async getDatabaseVersionBreakdown({
    db,
    includeArchived,
  }: {
    db: string;
    includeArchived: boolean;
  }): Promise<VersionEntry[]> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/summary/technology/database/${encodeURIComponent(db)}/versions?include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(r.statusText);
    return r.json();
  }

  async getReposByFilter(
    queryParams: string,
    includeArchived: boolean,
  ): Promise<TechRepoRow[]> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(
      `${base}/api/v1/summary/technology/repos?${queryParams}&include_archived=${includeArchived}`,
    );
    if (!r.ok) throw new Error(r.statusText);
    return r.json();
  }

  async getEntityDetail(repoId: string): Promise<CensusEntityDetail> {
    const base = await this.baseUrl();
    const r = await this.fetchApi.fetch(`${base}/api/v1/repos/${repoId}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }

  async getUiRepoSnapshots(): Promise<UiRepoSnapshot[]> {
    // Snapshots are served by the Backstage census backend plugin,
    // not by the external Census API — use discoveryApi directly.
    const backendUrl = await this.discoveryApi.getBaseUrl('census');
    const r = await this.fetchApi.fetch(`${backendUrl}/ui-repo-snapshots`);
    if (!r.ok) throw new Error(`Snapshots: HTTP ${r.status}`);
    return r.json();
  }
}
